
import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import { BaseContentModel } from "@/models/BaseContentModel";
import { CategoryModel } from "@/models/CategoryModel";
import { TopicModel } from "@/models/TopicModel";
import { ContentPopup } from "@/global-scripts/content-popup";
import { getModule } from "vuex-module-decorators";
import ContentModule from "@/store/modules/ContentModule";
import FilterModule from "@/store/modules/FilterModule";
import { PrintObject } from "@/models/PrintObject";
import PrintBasketModule from "@/store/modules/PrintBasketModule";

/* eslint-disable-next-line */
import ClickEvent = JQuery.ClickEvent;
import $ from "jquery";
import MemberModule from "@/store/modules/MemberModule";

const win: { addWidth: number; removeWidth: number } = window as any;
win.addWidth = 0;
win.removeWidth = 0;

@Component({
  components: {},
})
export default class AddToPrintButton extends Vue {
  readonly contentModule = getModule(ContentModule, this.$store);
  readonly filterModule = getModule(FilterModule, this.$store);
  readonly printBasketModule = getModule(PrintBasketModule, this.$store);
  readonly memberModule = getModule(MemberModule, this.$store);

  @Prop({ default: false })
  alwaysOpen!: boolean;

  @Prop()
  object!: BaseContentModel;

  @Prop()
  parentId!: string;

  @Prop({ default: 0 })
  indexInParent!: number;

  @Prop()
  isCategoryObject!: boolean;

  hideDescription = false;
  widthDescription = "";
  removeLabel = this.$t.PrintRemoveLabel;
  addLabel = this.$t.PrintAddLabel;
  removeWidth = 0;
  addWidth = 0;
  initialWidth = 0;
  loaded = false;
  close = true;
  isTouch = false;

  get componentClasses(): string {
    const classes = [];

    if (this.isInBasket) {
      classes.push("object-in-basket");
    }
    if (!this.close) {
      classes.push("show");
    }
    if (this.alwaysOpen) {
      classes.push("always-open");
    }

    return classes.join(" ");
  }

  get width(): string {
    if (!this.loaded) {
      // Setup not completed
      return "";
    }
    if (this.close) {
      return this.initialWidth + "px";
    }

    if (this.addWidth > 0) {
      return (this.isInBasket ? this.removeWidth : this.addWidth) + "px";
    }

    return "";
  }

  get description() {
    if (!this.loaded) {
      return this.widthDescription;
    }

    return this.isInBasket ? this.removeLabel : this.addLabel;
  }

  get isInBasket() {
    if (this.isCategoryObject) {
      const category = this.object as CategoryModel;

      // If Category all topics should be included in basket to mark the category as "in basket"
      if (category.topics) {
        let allTopicsInBasket = true;
        category.topics.forEach((t) => {
          allTopicsInBasket = allTopicsInBasket && this.topicInBasket(t);
        });
        return allTopicsInBasket;
      }
      return false;
    } else {
      return this.topicInBasket(this.object as TopicModel);
    }
  }

  onClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const topicCount = this.isCategoryObject ? ((this.object as CategoryModel).topics || []).length : 1;

    if (!this.isInBasket && this.printBasketModule.printBasketCount + topicCount > this.$config.topicBasketLimit) {
      // Print basket is full
      new ContentPopup(this.$t.PrintToManyTopicsTitle, this.$t.PrintToManyTopicsBody, this.$t.InfoboxAccept).show();
      return;
    }

    this.hideDescription = true;

    setTimeout(() => {
      this.hideDescription = false;

      this.addOrRemoveFromPrint();
    }, 250); // Animation delay
  }

  createPrintObject(contentModel: BaseContentModel, parentId: string | number, indexInParent: number = 0, printObjectId: number | undefined = undefined): PrintObject {
    return new PrintObject(contentModel.id.toString(), contentModel.title, parentId?.toString(), indexInParent, printObjectId);
  }

  addOrRemoveFromPrint() {
    if (this.isInBasket) {
      let printObjects: PrintObject[] = [];

      if (this.isCategoryObject) {
        const category = this.object as CategoryModel;
        printObjects = category.topics.map((topic) => {
          return this.getItemInBasket(topic);
        });
      } else {
        const basketItem = this.getItemInBasket(this.object as TopicModel);
        printObjects.push(basketItem);
      }

      this.printBasketModule.removeFromPrintBasket(printObjects);
      this.$tracker.trackPrintBasketEvent(`remove item`, window.location.href);
    } else {
      const printObjects: PrintObject[] = [];

      if (this.isCategoryObject) {
        const category = this.object as CategoryModel;
        category.topics.forEach((topic, index) => {
          if (!this.topicInBasket(topic)) {
            printObjects.push(this.createPrintObject(topic, category.id, index));
          }
        });
      } else {
        printObjects.push(this.createPrintObject(this.object, this.parentId, this.indexInParent));

        this.trackAddToPrint(this.object as TopicModel);
      }

      this.printBasketModule.addToPrintBasket(printObjects);
    }
  }

  trackAddToPrint(topic: TopicModel) {
    let category, subCategory;
    if (topic.parentNodes && topic.parentNodes.length > 0) {
      let categoryId = topic.parentNodes[0];
      subCategory = this.contentModule.categoryByID(categoryId);
    }

    if (subCategory) {
      category = this.contentModule.parentCategoryByID(subCategory.id);

      // If the category did not have any parent, this was a main category
      if (!category || !category.id) {
        category = subCategory;
        subCategory = undefined;
      }
    }

    this.$tracker.trackAddToPrint(
      this.object as TopicModel,
      this.filterModule.basePath,
      this.contentModule.locale,
      category,
      subCategory
    );
  }

  onMouseOver() {
    if (this.alwaysOpen || this.isTouch) {
      return;
    }

    this.close = false;
  }

  onMouseOut() {
    if (this.alwaysOpen || this.isTouch) {
      return;
    }

    this.close = true;
  }

  topicInBasket(topic: TopicModel): boolean {
    return (
        this.printBasketModule.printBasketCount > 0 &&
        this.printBasketModule.printBasket.printObjects.find((o) => this.printBasketModule.topicSameAsBasketItem(topic, o)) !== undefined
    );
  }

  getItemInBasket(topic: TopicModel): PrintObject {
    return this.printBasketModule.printBasket.printObjects.find((o) => this.printBasketModule.topicSameAsBasketItem(topic, o));
  }

  created() {
    this.isTouch = "ontouchstart" in document.documentElement;

    if (this.alwaysOpen) {
      this.close = false;
    } else if (this.isTouch) {
      this.close = true;
    }

    this.$nextTick(() => {
      setTimeout(() => {
        // Quick fix to make sure first width calc is working

        let descriptionLabel = $(".description", this.$el);

        this.initialWidth = descriptionLabel.outerWidth() as number;

        if (win.removeWidth > 0) {
          this.removeWidth = win.removeWidth;
        } else {
          this.widthDescription = this.removeLabel;
          this.$nextTick(() => {
            win.removeWidth = descriptionLabel.outerWidth() as number;
            this.removeWidth = win.removeWidth;
          });
        }

        // Reused by all AddToPrintButtons
        if (win.addWidth > 0) {
          this.addWidth = win.addWidth;
          this.loaded = true;
        } else {
          setTimeout(() => {
            this.widthDescription = this.addLabel;
            this.$nextTick(() => {
              win.addWidth = descriptionLabel.outerWidth() as number;
              this.addWidth = win.addWidth;
              this.loaded = true;
            });
          }, 250); // Transition delay
        }
      }, 900);
    });
  }
}
